import { authApi } from "./index";
import { triggerLocalFulfillments } from "./fulfillments";

// Local storage key for fallback operations
const LOCAL_STORAGE_KEY = "amalgamate_bookings";

const getLocalBookings = () => {
  const data = localStorage.getItem(LOCAL_STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalBookings = (bookings) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(bookings));
};

// Normalize bookings to bridge backend DTO and legacy frontend field names
export const normalizeBooking = (b) => {
  if (!b) return null;
  
  const scheduledAt = b.scheduledAt || (b.date && b.time ? `${b.date}T${b.time}:00Z` : new Date().toISOString());
  const date = b.date || (scheduledAt ? scheduledAt.split('T')[0] : '');
  const time = b.time || (scheduledAt ? scheduledAt.split('T')[1].substring(0, 5) : '');
  
  const offering = b.offering || {
    offeringId: b.offeringId,
    title: b.offeringTitle || "Service Offering",
    coverImageUrl: b.coverImageUrl || "",
    pricingModel: b.pricingModel || "flat",
    category: b.category || b.offeringCategory || "service"
  };
  
  const provider = b.provider || {
    userId: b.providerId,
    name: b.providerName || "Service Provider",
    avatarUrl: b.providerAvatarUrl || ""
  };
  
  const customer = b.customer || {
    userId: b.customerId,
    name: b.customerName || "Customer",
    avatarUrl: b.customerAvatarUrl || ""
  };

  const statusHistory = b.statusHistory || [
    { status: b.status || 'pending', timestamp: b.createdAt || new Date().toISOString(), note: 'Booking status initialized' }
  ];

  return {
    ...b,
    bookingId: b.bookingId || b.id,
    offeringId: b.offeringId || offering.offeringId,
    providerId: b.providerId || provider.userId,
    customerId: b.customerId || customer.userId,
    scheduledAt,
    date,
    time,
    durationHours: b.durationHours !== undefined ? b.durationHours : (b.duration !== undefined ? b.duration : 1),
    duration: b.duration !== undefined ? b.duration : (b.durationHours !== undefined ? b.durationHours : 1),
    numberOfPeople: b.numberOfPeople !== undefined ? b.numberOfPeople : 1,
    customerNotes: b.customerNotes || b.notesToProvider || "",
    notesToProvider: b.notesToProvider || b.customerNotes || "",
    basePrice: b.basePrice !== undefined ? b.basePrice : (b.price !== undefined ? b.price : 0),
    calculatedTotal: b.calculatedTotal !== undefined ? b.calculatedTotal : (b.price !== undefined ? b.price : 0),
    price: b.price !== undefined ? b.price : (b.calculatedTotal !== undefined ? b.calculatedTotal : 0),
    currency: b.currency || 'USD',
    status: b.status || 'pending',
    paymentStatus: b.paymentStatus || 'unpaid',
    providerNotes: b.providerNotes || "",
    declineReason: b.declineReason || "",
    cancelReason: b.cancelReason || "",
    createdAt: b.createdAt || new Date().toISOString(),
    offering,
    provider,
    customer,
    offeringTitle: offering.title,
    coverImageUrl: offering.coverImageUrl,
    category: offering.category || b.category || 'service',
    providerName: provider.name,
    providerAvatarUrl: provider.avatarUrl,
    customerName: customer.name,
    customerAvatarUrl: customer.avatarUrl,
    statusHistory
  };
};

// 1. Create a Booking Request
export const createBooking = async (payload) => {
  try {
    const response = await authApi.post("/Bookings", payload);
    const data = response.data.data || response.data;
    return normalizeBooking(data);
  } catch (error) {
    if (error.response) {
      throw error;
    }
    console.warn("Backend API POST /Bookings not reachable. Using fallback local storage.", error);
    
    // Fallback Mock Logic
    const bookings = getLocalBookings();
    const newId = 'BK-' + Math.floor(100000 + Math.random() * 900000);
    const currentUserId = localStorage.getItem("userId") || "cust-current-user-id";
    
    // Attempt to load offering details from local storage if available
    let offeringTitle = "Service Offering";
    let coverImageUrl = "";
    let pricingModel = "flat";
    let providerId = "prov-1";
    let providerName = "John Outdoors";
    let providerAvatarUrl = "/profilePic.jpg";

    try {
      const storedOfferings = JSON.parse(localStorage.getItem("amalgamate_local_offerings_v3") || "[]");
      const foundOff = storedOfferings.find(o => o.offeringId === payload.offeringId);
      if (foundOff) {
        offeringTitle = foundOff.title;
        coverImageUrl = foundOff.coverImageUrl;
        pricingModel = foundOff.pricingModel;
        providerId = foundOff.providerId;
      }
    } catch (e) {
      console.error("Error reading offerings for fallback details:", e);
    }

    const scheduledAt = payload.scheduledAt || new Date().toISOString();

    const mockBooking = {
      bookingId: newId,
      offeringId: payload.offeringId,
      providerId,
      customerId: currentUserId,
      scheduledAt,
      durationHours: payload.durationHours || 1,
      numberOfPeople: payload.numberOfPeople || 1,
      customerNotes: payload.customerNotes || "",
      basePrice: payload.pricingSnapshot?.basePrice || 0,
      calculatedTotal: payload.pricingSnapshot?.calculatedTotal || 0,
      currency: payload.pricingSnapshot?.currency || "USD",
      status: "pending",
      paymentStatus: "unpaid",
      createdAt: new Date().toISOString(),
      offering: {
        offeringId: payload.offeringId,
        title: offeringTitle,
        coverImageUrl,
        pricingModel
      },
      provider: {
        userId: providerId,
        name: providerName,
        avatarUrl: providerAvatarUrl
      },
      customer: {
        userId: currentUserId,
        name: localStorage.getItem("userName") || "Alice Smith",
        avatarUrl: "/profilePic.jpg"
      }
    };

    bookings.unshift(mockBooking);
    saveLocalBookings(bookings);
    return normalizeBooking(mockBooking);
  }
};

// 2. Get Bookings List
export const getBookingsList = async (params = {}) => {
  try {
    const response = await authApi.get("/Bookings", { params });
    const data = response.data.data || response.data;
    if (Array.isArray(data)) {
      return data.map(normalizeBooking);
    }
    return [];
  } catch (error) {
    console.warn("Backend API GET /Bookings not reachable. Using fallback local storage.", error);
    
    let bookings = getLocalBookings();
    const currentUserId = localStorage.getItem("userId") || "cust-current-user-id";
    
    // Filter based on role
    if (params.role === "customer") {
      bookings = bookings.filter(b => b.customerId === currentUserId || b.customer?.userId === currentUserId);
    } else if (params.role === "provider") {
      bookings = bookings.filter(b => b.providerId === currentUserId || b.provider?.userId === currentUserId);
    }

    // Filter by status if requested
    if (params.status) {
      bookings = bookings.filter(b => b.status === params.status);
    }

    return bookings.map(normalizeBooking);
  }
};

// 3. Provider Inbox (Dedicated Route)
export const getProviderInbox = async (params = {}) => {
  try {
    const apiParams = { role: "provider", ...params };
    if (!apiParams.status || apiParams.status === 'all') {
      delete apiParams.status;
    }
    const response = await authApi.get("/Bookings", { params: apiParams });
    const data = response.data.data || response.data;
    if (Array.isArray(data)) {
      return data.map(normalizeBooking);
    }
    return [];
  } catch (error) {
    console.warn("Backend API GET /Bookings not reachable. Using fallback local storage.", error);
    return getBookingsList({ role: "provider", ...params });
  }
};

// 4. Get Booking Details By ID
export const getBookingDetails = async (id) => {
  try {
    const response = await authApi.get(`/Bookings/${id}`);
    const data = response.data.data || response.data;
    return normalizeBooking(data);
  } catch (error) {
    console.warn(`Backend API GET /Bookings/${id} not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const found = bookings.find(b => b.bookingId === id || b.id === id);
    if (!found) {
      throw new Error("Booking not found");
    }
    return normalizeBooking(found);
  }
};

// 5. Confirm Booking Request
export const confirmBooking = async (id, providerNotes) => {
  try {
    const response = await authApi.patch(`/Bookings/${id}/confirm`, { providerNotes });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Bookings/${id}/confirm not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "confirmed",
      providerNotes,
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "confirmed", timestamp: new Date().toISOString(), note: `Confirmed by provider: ${providerNotes || 'No notes'}` }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    
    // Auto-create local fulfillments if there are bundled products
    triggerLocalFulfillments(updated);
    
    return { bookingId: id, status: "confirmed", updatedAt: new Date().toISOString() };
  }
};

// 6. Decline Booking Request
export const declineBooking = async (id, reason, messageToCustomer) => {
  try {
    const response = await authApi.patch(`/Bookings/${id}/decline`, { reason, messageToCustomer });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Bookings/${id}/decline not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "cancelled",
      declineReason: `${reason}: ${messageToCustomer || ""}`,
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "cancelled", timestamp: new Date().toISOString(), note: `Declined by provider. Reason: ${reason}. Message: ${messageToCustomer || 'None'}` }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return { bookingId: id, status: "cancelled", cancelledBy: "provider", updatedAt: new Date().toISOString() };
  }
};

// 7. Cancel Booking
export const cancelBooking = async (id, reason) => {
  try {
    const response = await authApi.patch(`/Bookings/${id}/cancel`, { reason });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Bookings/${id}/cancel not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "cancelled",
      cancelReason: reason,
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "cancelled", timestamp: new Date().toISOString(), note: `Cancelled. Reason: ${reason}` }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return { bookingId: id, status: "cancelled", cancelledBy: "user", updatedAt: new Date().toISOString() };
  }
};

// 8. Mark Service as Started
export const startBooking = async (id) => {
  try {
    const response = await authApi.patch(`/Bookings/${id}/start`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Bookings/${id}/start not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "in_progress",
      startedAt: new Date().toISOString(),
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "in_progress", timestamp: new Date().toISOString(), note: "Service started by provider" }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return { bookingId: id, status: "in_progress", startedAt: new Date().toISOString() };
  }
};

// 9. Mark Service as Completed
export const completeBooking = async (id, completionNotes) => {
  try {
    const response = await authApi.patch(`/Bookings/${id}/complete`, { completionNotes });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Bookings/${id}/complete not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "completed",
      completionNotes,
      completedAt: new Date().toISOString(),
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "completed", timestamp: new Date().toISOString(), note: `Marked complete by provider. Notes: ${completionNotes || 'None'}` }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return { bookingId: id, status: "completed", completedAt: new Date().toISOString(), completionConfirmedAt: null };
  }
};

// 10. Confirm Completion (Close Booking)
export const confirmCompletion = async (id) => {
  try {
    const response = await authApi.patch(`/Bookings/${id}/confirm-completion`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Bookings/${id}/confirm-completion not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "closed",
      completionConfirmedAt: new Date().toISOString(),
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "closed", timestamp: new Date().toISOString(), note: "Completion confirmed by customer" }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return { bookingId: id, status: "closed", completionConfirmedAt: new Date().toISOString() };
  }
};

// 11. Raise a Dispute
export const raiseDispute = async (id, payload) => {
  try {
    const response = await authApi.post(`/Bookings/${id}/dispute`, payload);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API POST /Bookings/${id}/dispute not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const updated = {
      ...bookings[index],
      status: "disputed",
      dispute: {
        reason: payload.reason,
        description: payload.description,
        evidenceUrls: payload.evidenceUrls || []
      },
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "disputed", timestamp: new Date().toISOString(), note: `Dispute raised by customer. Reason: ${payload.reason}` }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return { disputeId: 'DSP-' + Math.floor(100000 + Math.random() * 900000), bookingId: id, status: "open", createdAt: new Date().toISOString() };
  }
};

// 11.5. Respond to a Dispute (Provider)
export const respondToDispute = async (id, explanation, evidenceUrls = []) => {
  try {
    const response = await authApi.post(`/Bookings/${id}/dispute/respond`, { explanation, evidenceUrls });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API POST /Bookings/${id}/dispute/respond not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const index = bookings.findIndex(b => b.bookingId === id || b.id === id);
    if (index === -1) throw new Error("Booking not found");
    
    const currentDispute = bookings[index].dispute || { reason: 'other', description: 'unknown', evidenceUrls: [] };
    const updated = {
      ...bookings[index],
      dispute: {
        ...currentDispute,
        providerExplanation: explanation,
        providerEvidenceUrls: evidenceUrls,
        providerRespondedAt: new Date().toISOString()
      },
      statusHistory: [
        ...(bookings[index].statusHistory || []),
        { status: "disputed", timestamp: new Date().toISOString(), note: "Dispute response submitted by provider" }
      ]
    };
    
    bookings[index] = updated;
    saveLocalBookings(bookings);
    return {
      disputeId: bookings[index].disputeId || 'DSP-' + Math.floor(100000 + Math.random() * 900000),
      bookingId: id,
      status: "open",
      createdAt: new Date().toISOString(),
      providerExplanation: explanation,
      providerEvidenceUrls: evidenceUrls,
      providerRespondedAt: new Date().toISOString()
    };
  }
};

// 12. Get Booking Timeline History
export const getBookingTimeline = async (id) => {
  try {
    const response = await authApi.get(`/Bookings/${id}/timeline`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API GET /Bookings/${id}/timeline not reachable. Using fallback local storage.`, error);
    
    const bookings = getLocalBookings();
    const found = bookings.find(b => b.bookingId === id || b.id === id);
    if (!found) {
      throw new Error("Booking not found");
    }
    
    // Map status history to timeline items format
    const timeline = (found.statusHistory || []).map(h => ({
      status: h.status,
      timestamp: h.timestamp || new Date().toISOString(),
      actor: h.note?.toLowerCase().includes('provider') ? 'provider' : 'customer'
    }));

    return { bookingId: id, timeline };
  }
};
